import { AfterViewInit, Component, ElementRef, OnInit, Inject, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { ReferenceConst } from '@core-constants/reference.const';
import { RouteHelper } from '@core-helpers/route.helper';
import { SettingsManager } from '@core-managers/settings.manager';
import { Environment } from '@environments';
import { TranslateService } from '@shared-services/translate.service';
import { isPlatformBrowser } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { SesionStorageConst } from '@core-constants/storage.const';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BroadcastService } from "@shared-services/broadcast.service";
import { EAppEventName } from "@core-constants/app-event-names.const";
import { TokenManager } from '@core-managers/token.manager';

declare function loadChatBotScripts(): void;
declare function goToChatBot(): void;
declare function loadChatScripts(): void;
declare function hideModal(): void;
declare function loadEvents();

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class ChatComponent implements OnInit, AfterViewInit
{
  public readonly chatBtnId: string = ReferenceConst.ChatButtonId;
  public chatUrl: SafeUrl;

  public readonly whatsappImage: string = ReferenceConst.WhatsappImage;
  public readonly whatsAppURL: string = ReferenceConst.Whatsappurl;

  private readonly showChatbotEventId: string = "showChatbot";
  private readonly hideChatbotEventId: string = "hideChatbot";
  private readonly minimizeChatbotEventId: string = "minimizeChatbot";
  private readonly updateCartChatbotEventId: string = "updateCart";

  private readonly chatbotContainerId: string = "chatbot-v2-container";

  private readonly aivoUserCartId: string = "cartId";
  private readonly aivoUserSessionTrackingId: string = "sessionTrackingId";
  private readonly aivoUserSessionId: string = "sessionId";

  private currentCartCookie: string = null;
  private currentSessionCookie: string = null;

  @ViewChild(ReferenceConst.ChatButtonId) private buttonChat: ElementRef;

  constructor(@Inject(PLATFORM_ID) private platformId,
    protected translateService: TranslateService,
    protected routeHelper: RouteHelper,
    public cookieService: CookieService,
    public tokenManager: TokenManager,
    protected settingsManager: SettingsManager,
    private sanitizer: DomSanitizer) { }


  public ngOnInit(): void
  {
    this.chatUrl = this.sanitizer.bypassSecurityTrustResourceUrl(ReferenceConst.ChatUrl);
  }

  public ngAfterViewInit(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      this.detectChatbotChanges();
    }

    if (this.environmentChat)
    {
      loadChatBotScripts();
    }
    else
    {
      loadEvents();
    }
  }

  private detectChatbotChanges(): void
  {
    if (document.readyState === 'loading')
    {
      document.addEventListener('DOMContentLoaded', this.contentLoadedListener);
    }
    else
    {
      this.contentLoadedListener(null);
    }
  }

  private contentLoadedListener(event): void
  {
    window.addEventListener('message', this.iframeMessageListener);
  }

  private iframeMessageListener = (event: MessageEvent): void =>
  {
    const chatbotContainer = document.getElementById(this.chatbotContainerId);

    switch (event.data)
    {
      case this.showChatbotEventId:
        chatbotContainer.classList.remove('chatbotMaximized', 'chatbotHidden', 'chatbotMinimized');
        chatbotContainer.classList.add('chatbotMaximized');
        break;
      case this.hideChatbotEventId:
        chatbotContainer.classList.remove('chatbotMaximized', 'chatbotHidden', 'chatbotMinimized');
        chatbotContainer.classList.add('chatbotHidden');
        break;
      case this.minimizeChatbotEventId:
        chatbotContainer.classList.remove('chatbotMaximized', 'chatbotHidden', 'chatbotMinimized');
        chatbotContainer.classList.add('chatbotMinimized');
        break;
      case this.updateCartChatbotEventId:
        {
          const cartCookie = this.removeQuotes(this.cookieService.get(SesionStorageConst.CartCookieKey));
          BroadcastService.Instance.broadcast(EAppEventName.OnChatbotAddToCart, cartCookie);
        }
        break;
        
    }
  };

  private removeQuotes(text: string): string
  {
    if (text && text.startsWith('"') && text.endsWith('"'))
    {
      return text.slice(1, -1);
    }

    return text;  
  }

  public ngAfterViewChecked(): void
  {
    if (!isPlatformBrowser(this.platformId))
    {
      return;
    }

    const chatbotIframe = document.getElementById('chatbot-v2-container') as HTMLIFrameElement;

    if (chatbotIframe && chatbotIframe.contentWindow)
    {
      this.setCartCookie(chatbotIframe);
      this.setSessionCookie(chatbotIframe);
    }
  }

  private setCartCookie(chatbotIframe): void
  {
    const cartCookie = this.cookieService.get(SesionStorageConst.CartCookieKey);

    if (cartCookie !== this.currentCartCookie)
    {
      this.currentCartCookie = cartCookie;
      chatbotIframe.contentWindow.postMessage({ action: 'setCartCookie', data: cartCookie }, '*'); // Use '*' or specify a domain
    }
  }

  private setSessionCookie(chatbotIframe): void
  {
    const sessionCookie = this.cookieService.get(SesionStorageConst.ExtranetSessionCookieKey);

    if (sessionCookie !== this.currentSessionCookie)
    {
      this.currentSessionCookie = sessionCookie;
      chatbotIframe.contentWindow.postMessage({ action: 'setSessionCookie', data: sessionCookie }, '*'); // Use '*' or specify a domain
    }
  }

  public get isChatEnabled(): boolean
  {
    return this.settingsManager.isChatIconEnabled === true;
  }

  public get isAivoChatbotEnabled(): boolean
  {
    return this.settingsManager.isAivoChatbotEnabled === true;
  }

  public get noticeOfPrivacyURL(): string
  {
    return this.routeHelper.noticeOfPrivacyURL;
  }

  public get isWhatsAppEnabled(): boolean
  {
    return this.settingsManager.isWhatsAppIconEnabled === true;
  }

  public get environmentChat(): boolean
  {
    return Environment.ChatBot;
  }

  public get chatBotProd(): boolean 
  {
    return Environment.ChatBotProd;
  }

  public get urlAvatarChatBot(): string 
  {
    return Environment.UrlAvatarChatBot;
  }

  public get whatsAppUrl(): string
  {
    return this.tokenManager.getUser()?.isAkkyPro ? ReferenceConst.WhatsAppUrlAkkyPro : ReferenceConst.Whatsappurl;
  }

  public openChat(): void
  {
    if (this.environmentChat)
    {
      goToChatBot();
    }
    else
    {
      loadChatScripts();
    }
  }

  public onClosePrechat(): void
  {
    hideModal();
    this.buttonChat.nativeElement.style.display = "block";
  }

}
